import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import {
  setSelectedLocation,
  generateParams,
  receiveStoreLocatorGeolocation,
  refetchConsolidatedLocations,
  RECEIVE_SORTED_BY,
  fetchStoreLocations,
} from '../../actions/storeLocator';
import { STORE_SEARCH_LIMIT } from '../../constants/storeLocator';
import { BOXLESS } from '../../constants/returnMethods';
import { scrollIntoViewSmoothly } from '../../lib/scrollIntoView';
import Loader from '../Loader';
import { logData } from '../../actions/analytics';
import { EVENTS, EVENT_VALUES } from '../../constants/noflake/noflakeSchema';
import { SORT_OPTIONS, getLocations, getSortable } from '../../lib/locations';
import { POSTAL_CODE_REGEX } from '../../constants/validationPatterns';

const mapStateToProps = ({
  language,
  page: { productType, languageCode: localeLanguage, countryCode: localeCountry } = {},
  returnsSettings: {
    [productType]: {
      returnOptions: { authToken },
    },
  },
  retailerSettings: {
    branding: {
      link_color: linkColor,
      link_hover_color: linkHoverColor,
      brand_call_to_action: ctaColor,
    },
  } = {},
  returnOptions: { selectedOptionMethodKey, selectedOptionId },
  stripe: { completedPayment },
  storeLocator: {
    postalCode,
    country,
    cachedQueries,
    locationsById,
    isConsolidated,
    consolidatedLocations,
    isFetchingLocations,
    sortedBy,
    selectedLocation,
  },
}) => {
  const locations = getLocations({
    country,
    postalCode,
    cachedQueries,
    locationsById,
    isConsolidated,
    consolidatedLocations,
    selectedOptionId,
    selectedOptionMethodKey,
    sortedBy,
  });
  const sortable = getSortable({ locations, sortedBy });
  return {
    localeLanguage,
    localeCountry,
    language,
    authToken,
    selectedOptionMethodKey,
    selectedOptionId,
    completedPayment,
    ctaColor,
    linkColor,
    linkHoverColor,
    locations,
    isConsolidated,
    isFetchingLocations,
    postalCode,
    country,
    sortable,
    sortedBy,
    selectedLocation,
  };
};

const LocationJS = ({
  dispatch,
  localeLanguage,
  localeCountry,
  language,
  selectedOptionMethodKey,
  selectedOptionId,
  completedPayment,
  ctaColor,
  linkColor,
  linkHoverColor,
  authToken,
  locations,
  isConsolidated,
  isFetchingLocations,
  postalCode,
  country,
  sortedBy,
  selectedLocation,
  sortable,
}) => {
  const postalCodeValidationPattern = POSTAL_CODE_REGEX[country];
  const locationIds = locations ? locations.map((location) => location.location_id) : [];
  const locationIdsStr = locationIds.join(',');
  const logLocationClick = useCallback(
    (location) => {
      if (!location || isEmpty(selectedLocation) || location.location_id === selectedLocation.id) {
        return;
      }
      dispatch(
        logData({
          type: EVENTS.CLICK,
          value: EVENT_VALUES[EVENTS.CLICK].location_tile,
          data: {
            store_locations_sorted_by: sortedBy,
            store_locations_sorts_displayed: sortable ? SORT_OPTIONS : [],
            store_location_selected: {
              id: location.location_id,
              name: location.brand,
              ...location.address,
            },
            store_location_selected_index: location.index,
          },
        }),
      );
    },
    [selectedLocation, locationIdsStr, sortedBy, sortable],
  );

  const logLocationsSort = useCallback(
    (newSort) => {
      dispatch(
        logData({
          type: EVENTS.CLICK,
          value: EVENT_VALUES[EVENTS.CLICK].location_sort_by,
          data: {
            store_locations_sorted_by: newSort,
            store_locations_sorts_displayed: sortable ? SORT_OPTIONS : [],
            store_location_selected: {
              id: selectedLocation.location_id,
              name: selectedLocation.brand,
              ...selectedLocation.address,
            },
            store_location_selected_index: selectedLocation.index,
          },
        }),
      );
    },
    [selectedLocation, locationIdsStr, sortable],
  );

  const branding = {
    primaryColor: ctaColor,
    sContainer: {
      backgroundColor: 'inherit',
    },
    sContainerHover: {
      backgroundColor: 'inherit',
    },
    sContainerActive: {
      borderColor: ctaColor,
      borderWidth: '2px',
      backgroundColor: 'inherit',
    },
    sContainerActiveHover: {
      borderColor: ctaColor,
      borderWidth: '2px',
      backgroundColor: 'inherit',
    },
    sLink: {
      color: ctaColor,
      fontSize: '13px',
    },
    sLinkHover: {
      color: ctaColor,
      fontSize: '13px',
    },
    sParagraph: {
      fontSize: '13px',
      margin: '0px 0px 0.25rem',
      padding: '0.25rem 0px',
      lineHeight: '1',
      color: 'inherit',
    },
    sHeading: {
      fontSize: '15px',
      fontWeight: 'bold',
      padding: '.25rem 0',
      color: 'inherit',
    },
    sSubHeading: {
      fontSize: '13px',
    },
    sPrimaryButton: {
      backgroundColor: ctaColor,
      paddingTop: '1.1rem',
      paddingBottom: '1.1rem',
      fontSize: '15px',
    },
    sSecondaryButton: {
      backgroundColor: 'transparent',
      color: ctaColor,
      paddingTop: '1.1rem',
      paddingBottom: '1.1rem',
      fontSize: '15px',
    },
    sSelectList: {
      fontSize: '13px',
      color: ctaColor,
      backgroundColor: 'inherit',
    },
    sPostalCodeInput: {
      color: 'inherit',
      padding: '0px 0.5rem',
      fontSize: '13px',
    },
  };

  const locationsTranslations = {
    returnsCancelButtonLabel: language['returns.return_options.locations.cancel_button'],
    returnsDropoffLocationsNearLabel:
      language['returns.return_options.locations.dropoff_locations_near'],
    returnsPickupLocationsNearLabel:
      language['returns.return_options.locations.pickup_locations_near'],
    returnsInvalidPostalCodeLabel: language['returns.return_options.locations.invalid_postal_code'],
    returnsPostalCodeLabel: language['returns.return_options.locations.postal_code'],
    returnsSearchButtonLabel: language['returns.return_options.locations.search_button'],
    returnsSearchLocationsLabel: language['returns.return_options.locations.search_locations'],
    returnsSortByLabel: language['returns.return_options.locations.sort_by'],
    returnsSortByDistanceLabel: language['returns.return_options.locations.sort_by_distance'],
    returnsSortByRecommendedLabel: language['returns.return_options.locations.sort_by_recommended'],
  };

  useEffect(() => {
    if (isFetchingLocations) return;
    if (isConsolidated) {
      window.narvar('pickupLocation', {
        localeLanguage,
        localeCountry,
        onChange: (inputValue) =>
          dispatch(receiveStoreLocatorGeolocation({ postalCode: inputValue })),
        onLocationSelected: (location) => {
          const newLocation = {
            ...location,
            index: findIndex(locationIds, (id) => id === location.location_id),
          };
          logLocationClick(newLocation);
          dispatch(setSelectedLocation(newLocation));
        },
        onSubmit: (newPostalCode, newSort) => {
          if (newPostalCode !== postalCode) {
            dispatch(refetchConsolidatedLocations(newPostalCode, selectedOptionId));
          } else if (newSort !== sortedBy) {
            logLocationsSort(newSort);
            dispatch({
              type: RECEIVE_SORTED_BY,
              data: {
                sortedBy: newSort,
              },
            });
          }
        },
        onExpand: () =>
          dispatch(
            logData({
              type: EVENTS.CLICK,
              value: EVENT_VALUES[EVENTS.CLICK].location_show_more,
            }),
          ),
        onCollapse: () =>
          dispatch(
            logData({
              type: EVENTS.CLICK,
              value: EVENT_VALUES[EVENTS.CLICK].location_show_less,
            }),
          ),
        layout: 'PORTRAIT',
        branding,
        config: {
          postalCodeValidationPattern,
          numResultsOnInit: 6,
          numResultsOnExpand: 6,
          showMoreLocationsLabel: language['returns.return_options.locations.show_more'],
          showLessLocationsLabel: language['returns.return_options.locations.show_less'],
          ...locationsTranslations,
          locations: locations || [],
          authToken,
          postalCode,
          country,
          supportedAction: 'dropoff',
          displayBusinessInfo: true,
          sortable,
        },
      });
    } else {
      const {
        radius,
        supportedactions = [],
        resourceTypes = [],
      } = dispatch(generateParams(selectedOptionMethodKey));
      window.narvar('pickupLocation', {
        localeLanguage,
        localeCountry,
        onSubmit: async (newPostalCode) => {
          if (newPostalCode !== postalCode) {
            await dispatch(receiveStoreLocatorGeolocation({ postalCode: newPostalCode }));
            dispatch(fetchStoreLocations());
          }
        },
        onChange: (inputValue) =>
          dispatch(receiveStoreLocatorGeolocation({ postalCode: inputValue })),
        onLocationSelected: (location) => {
          if (selectedOptionId.includes(BOXLESS) && !completedPayment) {
            scrollIntoViewSmoothly(document.querySelector('.stripe-payment-container'));
          }
          const newLocation = {
            ...location,
            index: findIndex(locationIds, (id) => id === location.location_id),
          };
          logLocationClick(newLocation);
          dispatch(setSelectedLocation(newLocation));
        },
        layout: 'PORTRAIT',
        branding,
        config: {
          postalCodeValidationPattern,
          locations: locations || [],
          authToken,
          postalCode,
          radius,
          country,
          limit: STORE_SEARCH_LIMIT,
          resourceTypes: resourceTypes.length > 0 ? resourceTypes[0] : undefined,
          supportedAction: supportedactions.length > 0 ? supportedactions[0] : undefined,
          displayBusinessInfo: true,
          ...locationsTranslations,
        },
      });
    }
  }, [
    dispatch,
    authToken,
    selectedOptionMethodKey,
    selectedOptionId,
    ctaColor,
    linkColor,
    linkHoverColor,
    locationIdsStr,
    isConsolidated,
    isFetchingLocations,
    postalCode,
    country,
    logLocationClick,
    logLocationsSort,
  ]);

  if (isFetchingLocations) return <Loader />;

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <span data-narvar-feature="pickupLocation" />
    </div>
  );
};

LocationJS.propTypes = {
  dispatch: PropTypes.func.isRequired,
  localeLanguage: PropTypes.string.isRequired,
  localeCountry: PropTypes.string.isRequired,
  language: PropTypes.object.isRequired,
  authToken: PropTypes.string.isRequired,
  selectedOptionMethodKey: PropTypes.string.isRequired,
  selectedOptionId: PropTypes.string.isRequired,
  completedPayment: PropTypes.bool.isRequired,
  ctaColor: PropTypes.string,
  linkColor: PropTypes.string,
  linkHoverColor: PropTypes.string,
  locations: PropTypes.arrayOf(PropTypes.object),
  isConsolidated: PropTypes.bool,
  isFetchingLocations: PropTypes.bool,
  postalCode: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  sortedBy: PropTypes.string.isRequired,
  selectedLocation: PropTypes.object,
  sortable: PropTypes.bool.isRequired,
};

LocationJS.defaultProps = {
  ctaColor: '#000000',
  linkColor: '#000000',
  linkHoverColor: '#000000',
  locations: undefined,
  isConsolidated: false,
  isFetchingLocations: false,
  selectedLocation: {},
};

export default connect(mapStateToProps)(LocationJS);
